import { NxAvatarModule } from '@allianz/ng-aquila/avatar';
import { NxHeaderModule } from '@allianz/ng-aquila/header';
import { NxPopoverComponent, NxPopoverMainContentDirective, NxPopoverTriggerDirective } from '@allianz/ng-aquila/popover';
import { NxTabsModule } from '@allianz/ng-aquila/tabs';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { OAuthService } from 'angular-oauth2-oidc';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AccessibilityComponent } from '../components/accessibility/accessibility.component';
import { UserProfile, UserProfileService } from '../services/user-profile.service';

/**
 * UM API v2 answers with an empty array for the fields it holds no value for
 * (`lastName: []`, `phoneNumber: []`, ...), and `[]` is truthy in JS — so neither
 * `filter(Boolean)` nor `firstName && lastName` guards against it. Everything that
 * is not a non-blank string is therefore treated as absent.
 */
function asNamePart(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

@Component({
  selector: 'app-shell',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    NxHeaderModule,
    NxTabsModule,
    NxAvatarModule,
    NxPopoverComponent,
    NxPopoverTriggerDirective,
    NxPopoverMainContentDirective,
    TranslocoPipe,
    AccessibilityComponent,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  private readonly userProfileService = inject(UserProfileService);
  private readonly oauthService = inject(OAuthService);
  private readonly router = inject(Router);

  readonly subnavLinks = [
    { path: 'campaigns', label: 'Campaigns' },
  ];

  private readonly profile = toSignal(
    this.userProfileService.getCurrentUserProfile().pipe(catchError(() => of<UserProfile>({}))),
    { initialValue: {} as UserProfile },
  );

  /** The name parts actually usable for display, in order, blanks dropped. */
  private readonly nameParts = computed(() => {
    const { firstName, lastName } = this.profile();
    return [asNamePart(firstName), asNamePart(lastName)].filter((part) => part.length > 0);
  });

  /** "Firstname L." — first name plus the first letter of the last name, followed by a period. */
  readonly userName = computed(() => {
    const [first, last] = this.nameParts();
    if (first && last) {
      return `${first} ${last[0].toUpperCase()}.`;
    }

    return first ?? '';
  });

  readonly userInitials = computed(() => {
    const [first, last] = this.nameParts();
    if (first && last) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }

    return (first ?? '').slice(0, 2).toUpperCase();
  });

  /** "Country (BusinessUnitCode)", e.g. "France (SFAC)" — empty when unresolved. */
  readonly countryBusinessUnit = computed(() => {
    const country = asNamePart(this.profile().countryLabel);
    const businessUnitCode = asNamePart(this.profile().businessUnitCode);
    return country && businessUnitCode ? `${country} (${businessUnitCode})` : '';
  });

  logout(): void {
    this.oauthService.logOut();
    this.router.navigate(['/login']);
  }
}
